// Create Challan — the e-Pay Tax form, as Teamlio asks it.
//
// The heads are laid out the way the portal lays them out, in the portal's
// order and under the portal's names, because what gets typed here is what
// somebody then reads off a receipt. A form that renames or reorders them
// turns every check into a translation.
//
// Nothing here computes tax. Amount of Tax arrives pre-filled from the
// month's own entries and every head is editable, because what the challan
// must carry is what was ACTUALLY paid — a figure Teamlio worked out and a
// figure the bank took are two different things, and only the second one
// matches a 24Q.
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { tdsApi, ChallanSuggestion, TdsChallan } from "../../api/tds";

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

/** The months of each quarter of an Indian financial year. */
const QUARTER_MONTHS: Record<number, number[]> = {
  1: [4, 5, 6],
  2: [7, 8, 9],
  3: [10, 11, 12],
  4: [1, 2, 3],
};

// Salary is 192 under the 1961 Act and 392 under the 2025 one, which takes
// effect from 1 April 2026 — inside the years this screen files for. Both
// are offered because which is right depends on the date, not on us.
const SECTIONS = [
  { value: "192", label: "192 — Salaries", note: "Portal code 192 — payment of salary (1961 Act)" },
  { value: "392", label: "392 — Salaries (2025 Act)", note: "Portal code 392 — the 2025 Act's number for salary, from 1 April 2026" },
];

/** The eight heads, in the portal's own order and pairing. */
const HEADS: { key: HeadKey; label: string }[] = [
  { key: "tds_amount", label: "Amount of Tax" },
  { key: "interest_amount", label: "Interest" },
  { key: "surcharge_amount", label: "Surcharge" },
  { key: "other_amount", label: "Other" },
  { key: "edu_cess_amount", label: "Edu. Cess" },
  { key: "penalty_amount", label: "Penalty" },
  { key: "higher_edu_cess_amount", label: "Higher Edu. Cess" },
  { key: "late_fee_amount", label: "Late Fee under section 427" },
];

type HeadKey =
  | "tds_amount" | "interest_amount" | "surcharge_amount" | "other_amount"
  | "edu_cess_amount" | "penalty_amount" | "higher_edu_cess_amount" | "late_fee_amount";

export const CREATE_CHALLAN_CSS = `
.cc-field { display: block; margin-bottom: 12px; }
.cc-field > span { display: block; font-size: 11.5px; font-weight: 600; color: var(--gt-text-secondary); margin-bottom: 3px; }
.cc-note { font-size: 11px; color: var(--gt-text-tertiary); margin: -8px 0 12px; }
.cc-radio { display: flex; align-items: center; gap: 9px; font-size: 13px; margin-bottom: 8px; cursor: pointer; }
.cc-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 14px; }
.cc-late {
  margin-top: 10px; padding: 11px 13px; border-radius: 9px;
  background: #fef3c7; border: 1px solid #fcd34d; color: #78350f;
  font-size: 11.5px; line-height: 1.55;
}
.cc-late p { margin: 0 0 5px; }
.cc-late p:last-child { margin-bottom: 0; }
.cc-late-table { width: 100%; font-size: 11px; margin: 6px 0; border-collapse: collapse; }
.cc-late-table th { text-align: left; font-weight: 700; padding: 3px 6px; opacity: 0.75; }
.cc-late-table td { padding: 3px 6px; border-top: 1px solid rgba(120, 53, 15, 0.18); }
.cc-total {
  display: flex; justify-content: space-between; align-items: baseline;
  border-top: 1px solid var(--gt-border); margin-top: 6px; padding-top: 12px;
  font-size: 15px; font-weight: 700;
}
`;

/**
 * What a head should show when a fresh suggestion arrives.
 *
 * The suggestion owns the box until the filer types in it; after that the
 * figure is theirs and nothing moves it, because a challan has to carry
 * what was actually paid.
 *
 * `wasTyped` is the whole rule. An earlier version keyed on "is the box
 * empty", which treated the suggestion's OWN previous value as an edit:
 * opening on September and then choosing the whole quarter left September's
 * 9,000 in the tax box while the interest below had already moved to the
 * quarter's 810 -- a challan with two months of interest on one month of tax.
 */
/** An ISO date as "30 Jul 2026". Sliced, not put through Date(): an ISO
 *  date read as UTC comes back a day earlier in IST, and a deduction that
 *  moves a day can move a month of interest. */
function showDay(iso: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  const month = MONTHS[Number(m) - 1];
  return month ? `${Number(d)} ${month.slice(0, 3)} ${y}` : iso;
}

/**
 * The periods the count runs over, spelled out for a hover.
 *
 * Date to date, the way the figure beside it is worked out: somebody
 * checking against a notice needs to see WHICH periods were counted, not
 * just how many. "31 Jul→31 Aug, 31 Aug→30 Sep, 30 Sep→10 Oct" settles an
 * argument that "3" only starts.
 */
export function spanOf(deductedOn: string, depositDate: string): string {
  if (!deductedOn || !depositDate) return "";
  const start = new Date(`${deductedOn}T00:00:00`);
  const end = new Date(`${depositDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  if (end <= start) return "";

  // Same day next month, clamped to the month's end -- 31 Jan plus a month
  // is 28 Feb, and overflowing to 3 March would add a month to the count.
  const plus = (n: number) => {
    const d = new Date(start.getTime());
    const day = start.getDate();
    d.setDate(1);
    d.setMonth(start.getMonth() + n);
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(day, last));
    return d;
  };
  const show = (d: Date) =>
    `${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)}`;

  const spans: string[] = [];
  let cursor = start;
  for (let n = 1; n <= 240; n++) {
    const next = plus(n);
    if (next > end) break;
    spans.push(`${show(cursor)}→${show(next)}`);
    cursor = next;
  }
  if (cursor < end) spans.push(`${show(cursor)}→${show(end)} (part)`);
  return `Counted: ${spans.join(", ")} — ${spans.length} month${spans.length === 1 ? "" : "s"}`;
}

export function fillHead(current: string, suggested: number, wasTyped: boolean): string {
  if (wasTyped) return current;
  return suggested ? String(suggested) : "";
}

const inr = (n: number) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function CreateChallanDialog({
  financialYear,
  quarter,
  month,
  onClose,
  onCreated,
  onCreateAndPay,
}: {
  financialYear: string;
  quarter: number;
  /** The month the cycle has selected, 1-12. */
  month: number | null;
  onClose: () => void;
  onCreated: () => void;
  /** Create, then hand the challan to the extension to pay on the portal. */
  /** The challan just created, whole. Its id alone would mean reading it
   *  back from a list that has not been refreshed yet. */
  onCreateAndPay: (challan: TdsChallan) => void;
}) {
  // Seeded from the filter bar and then the dialog's own: a challan is
  // often raised for a quarter other than the one being looked at -- an
  // old one being settled late, most of all -- and making somebody close
  // this, change the bar and reopen is a worse answer than a second
  // dropdown.
  //
  // month 0 means the whole quarter on one challan: July, August and
  // September deposited together, which the interest below handles a
  // deduction date at a time.
  const [form, setForm] = useState({
    payment_type: "200",
    quarter,
    month: month && (QUARTER_MONTHS[quarter] || []).includes(month) ? month : 0,
    section: "192",
    deposit_date: "",
  });
  const months = QUARTER_MONTHS[form.quarter] || [];
  // Heads the filer has typed into. A suggestion never moves one of these
  // again: what a challan carries has to be what was actually paid, and a
  // figure that changes under somebody's hands is one they stop trusting.
  //
  // A ref, not state: it is read inside an async callback, and putting it
  // in the effect's dependencies would re-fetch the suggestion on every
  // keystroke.
  const touched = useRef<Set<HeadKey>>(new Set());

  const [heads, setHeads] = useState<Record<HeadKey, string>>({
    // All blank to begin with; the suggestion below fills them.
    tds_amount: "",
    interest_amount: "", surcharge_amount: "", other_amount: "",
    edu_cess_amount: "", penalty_amount: "", higher_edu_cess_amount: "",
    late_fee_amount: "",
  });
  const [saving, setSaving] = useState<"create" | "pay" | null>(null);
  const [hint, setHint] = useState<ChallanSuggestion | null>(null);

  // Re-asked whenever the month, the section or the deposit date changes:
  // all three move the answer.
  useEffect(() => {
    let alive = true;
    tdsApi.suggestChallan({
      financial_year: financialYear,
      quarter: form.quarter,
      month: form.month || undefined,
      section: form.section,
      deposit_date: form.deposit_date || undefined,
    })
      .then((r) => {
        if (!alive) return;
        setHint(r.data);
        // Follows the scope. Choosing "All of Q2" after the dialog opened
        // on one month has to move the tax from that month's figure to the
        // quarter's -- leaving it behind gave a challan with three months
        // of interest sitting on one month of tax.
        //
        // Keyed on whether the FILER typed it, not on whether the box is
        // empty: the old rule treated the suggestion's own earlier value
        // as somebody's edit and refused to replace it.
        const fill = (key: HeadKey, value: number, h: Record<HeadKey, string>) =>
          fillHead(h[key], value, touched.current.has(key));

        setHeads((h) => ({
          ...h,
          tds_amount: fill("tds_amount", r.data.pending, h),
          interest_amount: fill("interest_amount", r.data.interest.amount, h),
          late_fee_amount: fill("late_fee_amount", r.data.late_fee.amount, h),
        }));
      })
      .catch(() => { if (alive) setHint(null); });
    return () => { alive = false; };
  }, [financialYear, form.quarter, form.month, form.section, form.deposit_date]);

  const total = useMemo(
    () => HEADS.reduce((sum, head) => sum + (Number(heads[head.key]) || 0), 0),
    [heads]);

  const sectionNote = SECTIONS.find((s) => s.value === form.section)?.note;

  const submit = async (andPay: boolean) => {
    if (total <= 0) { toast.error("A challan needs an amount."); return; }
    setSaving(andPay ? "pay" : "create");
    try {
      const year = Number(financialYear.slice(0, 4)) + (form.month && form.month <= 3 ? 1 : 0);
      const { data } = await tdsApi.createChallan({
        year,
        month: form.month || undefined,
        financial_year: financialYear,
        quarter: form.quarter,
        section: form.section,
        payment_type: form.payment_type,
        deposit_date: form.deposit_date || null,
        ...HEADS.reduce((acc, head) => ({
          ...acc, [head.key]: Number(heads[head.key]) || 0,
        }), {} as Record<HeadKey, number>),
      });
      toast.success("Challan created");
      onCreated();
      if (andPay) onCreateAndPay(data.challan);
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not create that challan.");
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <style>{CREATE_CHALLAN_CSS}</style>
        <DialogHeader>
          <DialogTitle>Create Challan</DialogTitle>
          <DialogDescription>
            Type of Payment
          </DialogDescription>
        </DialogHeader>

        <div style={{ maxHeight: "58vh", overflow: "auto", paddingRight: 4 }}>
          {[
            ["200", "(200) TDS / TCS Payable by TaxPayer"],
            ["400", "(400) TDS / TCS Regular Assessment (Raised by I.T. Deptt.)"],
          ].map(([value, label]) => (
            <label key={value} className="cc-radio">
              <input type="radio" name="payment_type" value={value}
                checked={form.payment_type === value}
                onChange={() => setForm((f) => ({ ...f, payment_type: value }))} />
              {label}
            </label>
          ))}

          <label className="cc-field" style={{ marginTop: 12 }}>
            <span>Tax Year</span>
            {/* Read-only: the filter bar owns the year, and two places to set
                it is two places for them to disagree. */}
            <input className="gt-input" value={financialYear} readOnly
              style={{ width: "100%", background: "var(--gt-surface-2)" }} />
          </label>
          <p className="cc-note">From the filter bar above.</p>

          <label className="cc-field">
            <span>Quarter</span>
            <select className="gt-input" value={form.quarter} style={{ width: "100%" }}
              onChange={(e) => {
                // The old month is not in the new quarter, so carrying it
                // over would scope the challan to nothing. Falls back to
                // the whole quarter, which is also what sits at the top of
                // the month list.
                const next = Number(e.target.value);
                setForm((f) => ({ ...f, quarter: next, month: 0 }));
              }}>
              {[1, 2, 3, 4].map((q) => (
                <option key={q} value={q}>
                  Q{q} ({MONTHS[QUARTER_MONTHS[q][0] - 1].slice(0, 3)}–
                  {MONTHS[QUARTER_MONTHS[q][2] - 1].slice(0, 3)})
                </option>
              ))}
            </select>
          </label>

          <label className="cc-field">
            <span>Month</span>
            <select className="gt-input" value={form.month || 0} style={{ width: "100%" }}
              onChange={(e) => setForm((f) => ({ ...f, month: Number(e.target.value) }))}>
              {/* First, because one challan for the whole quarter is the
                  common case here and a list is read from the top. */}
              <option value={0}>
                All of Q{form.quarter} ({MONTHS[months[0] - 1]}–{MONTHS[months[months.length - 1] - 1]})
              </option>
              {months.map((m) => <option key={m} value={m}>{MONTHS[m - 1]}</option>)}
            </select>
          </label>

          <label className="cc-field">
            <span>Section</span>
            <select className="gt-input" value={form.section} style={{ width: "100%" }}
              onChange={(e) => setForm((f) => ({ ...f, section: e.target.value }))}>
              {SECTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>
          {sectionNote && <p className="cc-note">{sectionNote}</p>}

          <label className="cc-field">
            <span>Deposit Date</span>
            {/* Left blank on a draft on purpose: a challan that has not been
                paid has no deposit date, and today's date filled in by
                default is a date somebody will later read as a fact. */}
            <input className="gt-input" type="date" value={form.deposit_date}
              style={{ width: "100%" }}
              onChange={(e) => setForm((f) => ({ ...f, deposit_date: e.target.value }))} />
          </label>

          <div className="cc-grid">
            {HEADS.map((head) => (
              <label key={head.key} className="cc-field">
                <span>{head.label}</span>
                <input className="gt-input" type="number" min="0" step="0.01"
                  placeholder={head.label}
                  value={heads[head.key]} style={{ width: "100%" }}
                  onChange={(e) => {
                    touched.current.add(head.key);
                    setHeads((h) => ({ ...h, [head.key]: e.target.value }));
                  }} />
              </label>
            ))}
          </div>

          <div className="cc-total">
            <span>Total</span>
            <span>{inr(total)}</span>
          </div>
          <p className="cc-note" style={{ marginTop: 8 }}>
            Amount of Tax is filled in from the entries in scope — edit it, and the
            other heads, to match what you actually paid.
          </p>

          {hint && (hint.interest.late || hint.late_fee.amount > 0 || hint.already_paid > 0) && (
            <div className="cc-late">
              {hint.already_paid > 0 && (
                <p>
                  <strong>{inr(hint.already_paid)}</strong> is already paid for this
                  period, so only <strong>{inr(hint.pending)}</strong> is left.
                </p>
              )}

              {hint.interest.late && (
                <>
                  <p>
                    Section 201(1A) adds 1.5% for every month or part of one —{" "}
                    <strong>{inr(hint.interest.amount)}</strong>.
                  </p>
                  {/* Said outright, because the due date sitting in the next
                      column invites the other arithmetic: counting Aug to Oct
                      gives 3 where the section gives 4. The section reads
                      "from the date on which such tax was deducted to the date
                      on which such tax is actually paid". */}
                  {/* Said outright: the due date in the next column invites
                      the other arithmetic. A month here runs from a date to
                      the same date of the next month, so 31 July paid on 10
                      August is ten days -- one month, not two. */}
                  <p>
                    Counted from the date the tax was <strong>deducted</strong> to the
                    date it is paid — not from the due date. A month runs date to date,
                    and any part of one counts as a whole month.
                  </p>
                  {/* Month by month, because that is how it is worked out: a
                      quarter paid in one go is not three months of interest on
                      the whole sum. Showing the lines is what lets somebody
                      check the figure against a notice. */}
                  <table className="cc-late-table">
                    <thead>
                      <tr>
                        <th>Deducted</th><th>Was due</th>
                        <th style={{ textAlign: "right" }}>Tax</th>
                        <th style={{ textAlign: "right" }} title="Whole months and parts of months from the deduction to the deposit.">
                          Months
                        </th>
                        <th style={{ textAlign: "right" }}>Interest</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hint.interest.lines.map((line) => (
                        <tr key={line.deducted}>
                          <td>{showDay(line.deducted)}</td>
                          <td>{line.due}</td>
                          <td style={{ textAlign: "right" }}>{inr(line.amount)}</td>
                          <td style={{ textAlign: "right" }}
                            title={spanOf(line.deducted, form.deposit_date)}>
                            {line.months}
                          </td>
                          <td style={{ textAlign: "right" }}>{inr(line.interest)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {hint.late_fee.amount > 0 && (
                <p>
                  The {financialYear} Q{form.quarter} statement was due{" "}
                  {hint.statement_due_date} and has not been filed —{" "}
                  <strong>{hint.late_fee.days} day{hint.late_fee.days === 1 ? "" : "s"}</strong>{" "}
                  at ₹200 under 234E is <strong>{inr(hint.late_fee.amount)}</strong>
                  {hint.late_fee.capped && ", capped at the tax itself"}. It grows until
                  the return goes in.
                </p>
              )}

              <p style={{ marginTop: 6, opacity: 0.8 }}>
                Worked out from the dates, not from a notice. Overwrite any of them.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={!!saving}>Cancel</Button>
          <Button variant="outline" onClick={() => submit(false)} disabled={!!saving}>
            {saving === "create" ? "Creating…" : "Create"}
          </Button>
          <Button onClick={() => submit(true)} disabled={!!saving}>
            {saving === "pay" ? "Opening portal…" : "Create & Pay on Portal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
